import { useSelector } from 'react-redux';
import { ReactElement, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { TaxEnvironment } from '@mews-commander/data-access';
import { ApplicationState, TaxRateStrategyDiscriminator } from '@mews-commander/types';
import { SelectFieldProps } from '@mews-commander/form';
import { Labeled } from '@optimus-web/core';
import { useLocalizeCurrency, useLocalizePercentage } from '@mews/framework';
import {
    getTaxationsByTaxationCodes,
    getTaxEnvironmentsByTaxEnvironmentCodes,
} from '@mews-commander/legacy-entities/src/ducks/taxEnvironments';
import { getLegalEnvironmentsArray } from '@mews-commander/legacy-entities/src/ducks/legalEnvironments';
import { useCommanderSelector } from '@mews-commander/utils';
import { TaxesFieldNames } from '../components/ConfigureProductForm/formEnums';

export interface TaxConfiguration {
    code: string;
    countryCode?: string;
    environmentCode: string;
    legalEnvironmentName?: string;
    localName: string;
    name: string;
    taxRates: { primaryText: string; code: string; currency?: string }[];
}

export const useConfigureProductTax = (taxEnvironmentCodes: string[]) => {
    const localizeCurrency = useLocalizeCurrency();
    const localizePercentage = useLocalizePercentage();
    const { setValue } = useFormContext();

    const taxEnvironment: TaxEnvironment[] = useCommanderSelector((state) =>
        getTaxEnvironmentsByTaxEnvironmentCodes(state, { taxEnvironmentCodes })
    );

    const uniqueTaxationCodes = taxEnvironment.flatMap(({ taxationCodes }) => taxationCodes);

    const taxations = useSelector((state: ApplicationState) =>
        getTaxationsByTaxationCodes(state, { taxationCodes: uniqueTaxationCodes })
    );

    const legalEnvironment = useCommanderSelector((state) =>
        getLegalEnvironmentsArray(state, { legalEnvironmentCodes: taxEnvironmentCodes })
    );

    const taxConfigurationList = useMemo(() => {
        const list = taxEnvironment.flatMap((environment) => {
            const foundLegalEnvironment = legalEnvironment.find(({ code }) => code === environment.code);

            return taxations
                .filter(({ code }) => environment.taxationCodes.includes(code))
                .map((taxation) => ({
                    code: taxation.code,
                    countryCode: foundLegalEnvironment?.countryCode,
                    environmentCode: environment.code,
                    legalEnvironmentName: foundLegalEnvironment?.englishName,
                    localName: taxation.localName,
                    name: taxation.name,
                    taxRates: taxation.taxRates.map(({ strategy, code }) => ({
                        code,
                        currency:
                            strategy.discriminator === TaxRateStrategyDiscriminator.Flat
                                ? strategy.value.currencyCode
                                : undefined,
                        primaryText:
                            strategy.discriminator === TaxRateStrategyDiscriminator.Flat
                                ? localizeCurrency(strategy.value.value, strategy.value.currencyCode)
                                : localizePercentage(strategy.value.value, {
                                      minimumFractionDigits: 0,
                                      maximumFractionDigits: 6,
                                  }),
                    })),
                }));
        });

        return list.sort((a, b) => (a.countryCode ?? '').localeCompare(b.countryCode ?? ''));
    }, [taxEnvironment, legalEnvironment, taxations, localizeCurrency, localizePercentage]);

    const searchTaxRates: SelectFieldProps['customSearch'] = useCallback(
        (component: ReactElement<Labeled>, searchValue: string): boolean => {
            const label = component.props.primaryText as string;
            return label?.includes(searchValue);
        },
        []
    );

    useEffect(() => {
        if (taxConfigurationList.length) {
            taxConfigurationList.forEach(({ code }) => {
                setValue(`${TaxesFieldNames.TaxationCode}.${code}`, code);
            });
        }
    }, [setValue, taxConfigurationList]);

    return { searchTaxRates, taxConfigurationList };
};
